import { useFormik } from 'formik';
import Input from '../../../components/input';
import Select from '../../../components/select';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
  createProject,
  deleteUserProject,
  listUserProjects,
  updateUserProject,
} from '../../../hooks/local/userReducer';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../../../components/modal';
import { apiEndPoints } from '../../../hooks/remote/apiEndPoints';
import { toast } from 'react-toastify';

const ManageProject = () => {
  document.title = 'Manage projects - CloudVantage';

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userProjectList = useSelector(
    (state) => state.user.userProjectList,
  );

  // Filters
  const [zones, setZones] = useState([]);
  const [zonesLoading, setZonesLoading] = useState(false);
  const [zoneFilter, setZoneFilter] = useState('');
  const [searchText, setSearchText] = useState('');

  // Selected project
  const [selectedProject, setSelectedProject] = useState(null);

  // Modals
  const [manageProjectModal, setManageProjectModal] =
    useState(false);

  const [createProjectModal, setCreateProjectModal] =
  useState(false);

  const [editProjectModal, setEditProjectModal] =
    useState(false);

  const [deleteProjectModal, setDeleteProjectModal] =
    useState(false);

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    dispatch(listUserProjects());
    fetchZones();
  }, [dispatch]);

const fetchZones = async () => {
  setZonesLoading(true);

  try {
    const response = await apiEndPoints.listZones();
    const data = response?.data || response;

    const zoneList = Array.isArray(data)
      ? data
      : data?.zones ||
        data?.results ||
        data?.data ||
        [];

    setZones(
      Array.isArray(zoneList)
        ? zoneList
        : [],
    );
  } catch (error) {
    const errorMessage =
      error?.response?.data?.message ||
      error?.response?.data?.statusmessage ||
      error?.message ||
      'Unable to load regions.';

    setZones([]);

    toast.error(errorMessage, {
      theme: 'colored',
      hideProgressBar: true,
      autoClose: 3000,
    });
  } finally {
    setZonesLoading(false);
  }
};

const zoneOptions = [
  {
    value: '',
    label: zonesLoading
      ? 'Loading regions...'
      : 'Select a region',
  },

  ...zones.map((zone) => ({
    value:
      zone?.id ||
      zone?.zone_id ||
      '',

    label:
      zone?.name ||
      zone?.zone_name ||
      'Unnamed region',
  })),
];



  const getProjectId = (project) =>
    project?.id ||
    project?.project_id ||
    project?.uuid ||
    project?.projectId ||
    '';

const getProjectZoneId = (project) =>
  project?.zone_id ||
  project?.zone?.id ||
  project?.zone?.zone_id ||
  project?.zone_details?.id ||
  project?.zone_details?.zone_id ||
  '';

  const getProjectZoneName = (project) =>
    project?.zone_name ||
    project?.zone?.name ||
    project?.zone_details?.name ||
    project?.zone?.zone_name ||
    project?.zoneName ||
    'N/A';

const filteredProjects = useMemo(() => {
  const projects = Array.isArray(userProjectList)
    ? userProjectList
    : [];

  return projects.filter((project) => {
    const projectName =
      project?.name?.toLowerCase() || '';

    const search =
      searchText.trim().toLowerCase();

    const matchesSearch =
      !search ||
      projectName.includes(search);

    const matchesZone =
      !zoneFilter ||
      String(getProjectZoneId(project)) ===
        String(zoneFilter);

    return matchesSearch && matchesZone;
  });
}, [userProjectList, searchText, zoneFilter]);

// ======================================================
// CREATE PROJECT FORM
// ADD IT HERE
// ======================================================

const createProjectForm = useFormik({
  initialValues: {
    projectName: '',
    projectDescription: '',
    zoneId: '',
  },

  validationSchema: Yup.object({
    projectName: Yup.string().required(
      'Project Name cannot be empty',
    ),

    zoneId: Yup.string().required(
      'Please select a region',
    ),
  }),

  onSubmit: async (
    values,
    { resetForm, setSubmitting },
  ) => {
    const createProjectData = {
      name: values.projectName,
      description: values.projectDescription,
      zone_id: values.zoneId,
    };

    try {
      setSubmitting(true);

      const { payload } = await dispatch(
      createProject({
        createProjectData,
        navigate,
      }),
    );

      if (payload?.statuscode === '00') {
        await dispatch(listUserProjects());

        resetForm();
        setCreateProjectModal(false);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.statusmessage ||
          error?.response?.data?.message ||
          error?.message ||
          'Unable to create project.',
      );
    } finally {
      setSubmitting(false);
    }
  },
});

// ======================================================
// EXISTING UPDATE PROJECT FORM
// ======================================================

const updateProject = useFormik({
    enableReinitialize: true,

    initialValues: {
      projectId:
        getProjectId(selectedProject),

      projectName:
        selectedProject?.name || '',

      projectDescription:
        selectedProject?.description || '',
    },

    validationSchema: Yup.object({
      projectName: Yup.string().required(
        'Project Name cannot be empty',
      ),

      projectDescription:
        Yup.string().required(
          'Project Description cannot be empty',
        ),
    }),

    onSubmit: async (values) => {
      const {
        projectId,
        projectName,
        projectDescription,
      } = values;

      const updateProjectData = {
        name: projectName,
        description:
          projectDescription,
        projectId,
      };

      try {
        setLoading(true);

        const { payload } =
          await dispatch(
            updateUserProject({
              updateProjectData,
              navigate,
            }),
          );

        if (
          payload?.statuscode === '00'
        ) {
          await dispatch(
            listUserProjects(),
          );

          setEditProjectModal(false);
          setSelectedProject(null);
        } else {
          toast.error(
            payload?.statusmessage ||
              payload?.message ||
              'Unable to update project.',
          );
        }
      } catch (error) {
        toast.error(
          error?.response?.data
            ?.statusmessage ||
            error?.response?.data?.message ||
            error?.message ||
            'Unable to update project.',
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const deleteProject = async () => {
    if (!selectedProject) return;

    const projectId =
      getProjectId(selectedProject);

    if (!projectId) {
      console.log(
        'Selected project:',
        selectedProject,
      );

      toast.error(
        'Unable to identify project.',
      );

      return;
    }

    try {
      setLoading(true);

      const { payload } =
        await dispatch(
          deleteUserProject(projectId),
        );

      if (
        payload?.statuscode === '00'
      ) {
        await dispatch(
          listUserProjects(),
        );

        setDeleteProjectModal(false);
        setSelectedProject(null);
      } else {
        toast.error(
          payload?.statusmessage ||
            payload?.message ||
            'Unable to delete project.',
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data
          ?.statusmessage ||
          error?.response?.data?.message ||
          error?.message ||
          'Unable to delete project.',
      );
    } finally {
      setLoading(false);
    }
  };

  const closeManageModal = () => {
    setManageProjectModal(false);
    setSelectedProject(null);
  };

  const closeEditModal = () => {
    setEditProjectModal(false);
    setSelectedProject(null);
  };

  const closeDeleteModal = () => {
    setDeleteProjectModal(false);
    setSelectedProject(null);
  };

  return (
    <div className="px-4 md:px-10 py-6 min-h-screen dark:text-white">

      {/* PAGE TITLE */}
      <div className="flex items-center justify-between mb-6">
        <p className="text-xl font-medium text-primaryBlue">
          Manage Projects
        </p>

        <button
          type="button"
          onClick={async () => {setCreateProjectModal(true);
           await fetchZones(); }}
          className="px-4 py-3 rounded-md bg-primaryBlue text-white text-xs font-medium hover:opacity-90 transition-all"
        >
          + Create New Project
        </button>
      </div>

      {/* FILTER SECTION */}
      <div className="bg-white dark:bg-slate-800 rounded-md border dark:border-[#c4c4c432] p-4 md:p-6 mb-6">

        <p className="text-sm font-medium mb-4">
          Filter Projects
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">

          <Select
            label="Region"
            name="zoneFilter"
            options={zoneOptions}
            valueKey="value"
            labelKey="label"
            value={zoneFilter}
            onChange={(e) =>
              setZoneFilter(
                e.target.value,
              )
            }
            disabled={zonesLoading}
          />

          <Input
            label="Project Name"
            type="search"
            name="projectSearch"
            placeholder="Search by project name..."
            value={searchText}
            onChange={(e) =>
              setSearchText(
                e.target.value,
              )
            }
          />

        </div>
      </div>

      {/* PROJECT TABLE */}
      <div className="bg-white dark:bg-slate-800 rounded-md border dark:border-[#c4c4c432] overflow-x-auto">

        <table className="w-full text-sm text-left">

          <thead className="border-b dark:border-[#c4c4c432] text-gray-500 dark:text-gray-400">

            <tr>
              <th className="px-6 py-4 whitespace-nowrap">
                Project Name
              </th>

              <th className="px-6 py-4 whitespace-nowrap">
                Description
              </th>

              <th className="px-6 py-4 whitespace-nowrap">
                Region
              </th>

              <th className="px-6 py-4 whitespace-nowrap">
                Actions
              </th>
            </tr>

          </thead>

          <tbody>
            {filteredProjects.length > 0 ? (
              filteredProjects.map(
                (project, index) => {
                  const projectId =
                    getProjectId(project) ||
                    index;

                  return (
                    <tr
                      key={projectId}
                      className="border-b last:border-b-0 dark:border-[#c4c4c424] hover:bg-primaryBlue/5 transition-all"
                    >
                      <td className="px-6 py-4 font-medium">
                        {project?.name ||
                          '—'}
                      </td>

                      <td className="px-6 py-4 text-gray-500 dark:text-gray-400">
                        {project?.description ||
                          '—'}
                      </td>

                      <td className="px-6 py-4">
                        {getProjectZoneName(
                          project,
                        )}
                      </td>

                      <td className="px-6 py-4">

                        <button
                          type="button"
                          onClick={() => {
                            setSelectedProject(
                              project,
                            );

                            setManageProjectModal(
                              true,
                            );
                          }}
                          className="px-5 py-2.5 rounded-md bg-primaryBlue text-white text-xs font-medium hover:opacity-90 transition-all"
                        >
                          Manage
                        </button>

                      </td>
                    </tr>
                  );
                },
              )
            ) : (
              <tr>
                <td
                  colSpan={4}
                  className="px-6 py-12 text-center text-gray-500"
                >
                  No projects found.
                </td>
              </tr>
            )}
          </tbody>

        </table>

      </div>

{/* CREATE PROJECT MODAL */}
<Modal
  title="Create New Project"
  isVisible={createProjectModal}
  onClose={() => {
    setCreateProjectModal(false);
    createProjectForm.resetForm();
  }}
  size="md:w-[32%] lg:w-[30%]"
>
  <form
    onSubmit={createProjectForm.handleSubmit}
    className="grid gap-5 pt-3"
  >
    <Input
      label="Project Name"
      type="text"
      name="projectName"
      value={createProjectForm.values.projectName}
      onChange={createProjectForm.handleChange}
      onBlur={createProjectForm.handleBlur}
      onError={
        createProjectForm.touched.projectName &&
        createProjectForm.errors.projectName
          ? createProjectForm.errors.projectName
          : null
      }
    />

    <Input
      label="Project Description"
      type="text"
      name="projectDescription"
      placeholder="Enter project description"
      value={createProjectForm.values.projectDescription}
      onChange={createProjectForm.handleChange}
      onBlur={createProjectForm.handleBlur}
    />

    <Select
      label="Region"
      name="zoneId"
      options={zoneOptions}
      valueKey="value"
      labelKey="label"
      value={createProjectForm.values.zoneId}
      onChange={createProjectForm.handleChange}
      onBlur={createProjectForm.handleBlur}
      disabled={zonesLoading}
      onError={
        createProjectForm.touched.zoneId &&
        createProjectForm.errors.zoneId
          ? createProjectForm.errors.zoneId
          : null
      }
    />

    <div className="flex justify-end gap-3 pt-4 border-t dark:border-[#c4c4c424]">
      <button
        type="button"
        onClick={() => {
          setCreateProjectModal(false);
          createProjectForm.resetForm();
        }}
        disabled={createProjectForm.isSubmitting}
        className="px-5 py-2.5 rounded-md border dark:border-[#c4c4c432] text-sm disabled:opacity-50"
      >
        Cancel
      </button>

      <button
        type="submit"
        disabled={
          createProjectForm.isSubmitting ||
          zonesLoading ||
          zones.length === 0
        }
        className="px-5 py-2.5 rounded-md bg-primaryBlue text-white text-sm disabled:opacity-50"
      >
        {createProjectForm.isSubmitting
          ? 'Creating...'
          : 'Create Project'}
      </button>
    </div>
  </form>
</Modal>

{/* MANAGE PROJECT MODAL */}
<Modal
  title={`Manage "${selectedProject?.name || ''}"`}
        isVisible={manageProjectModal}
        onClose={closeManageModal}
        size="md:w-[32%] lg:w-[30%]"
      >
        <div className="grid text-sm py-2">

          {/* EDIT */}
          <button
            type="button"
            onClick={() => {
              setManageProjectModal(false);
              setEditProjectModal(true);
            }}
            className="flex items-center justify-between py-5 px-3 border-b dark:border-[#c4c4c424] hover:bg-primaryBlue/5 transition-all text-left"
          >
            <span>
              Edit Project
            </span>

            <span className="text-primaryBlue text-lg">
              ›
            </span>
          </button>

          {/* DELETE */}
          <button
            type="button"
            onClick={() => {
              setManageProjectModal(false);
              setDeleteProjectModal(true);
            }}
            className="flex items-center justify-center py-5 px-3 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 transition-all"
          >
            Delete Project
          </button>

        </div>
      </Modal>

      {/* EDIT PROJECT MODAL */}
      <Modal
        title="Edit Project"
        isVisible={editProjectModal}
        onClose={closeEditModal}
      >
        <form
          onSubmit={
            updateProject.handleSubmit
          }
          className="grid gap-5 pt-3"
        >

          <Input
            label="Project Name"
            type="text"
            name="projectName"
            value={
              updateProject.values
                .projectName
            }
            onChange={
              updateProject.handleChange
            }
            onBlur={
              updateProject.handleBlur
            }
            onError={
              updateProject.touched
                .projectName &&
              updateProject.errors
                .projectName
                ? updateProject.errors
                    .projectName
                : null
            }
          />

          <Input
            label="Project Description"
            type="text"
            name="projectDescription"
            placeholder="Update your project description"
            value={
              updateProject.values
                .projectDescription
            }
            onChange={
              updateProject.handleChange
            }
            onBlur={
              updateProject.handleBlur
            }
            onError={
              updateProject.touched
                .projectDescription &&
              updateProject.errors
                .projectDescription
                ? updateProject.errors
                    .projectDescription
                : null
            }
          />

          <div className="flex justify-end gap-3 pt-3 border-t dark:border-[#c4c4c424]">

            <button
              type="button"
              onClick={closeEditModal}
              disabled={loading}
              className="px-5 py-2 rounded-md border dark:border-[#c4c4c432] text-sm disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 rounded-md bg-primaryBlue text-white text-sm disabled:opacity-50"
            >
              {loading
                ? 'Saving...'
                : 'Save Changes'}
            </button>

          </div>

        </form>
      </Modal>

      {/* DELETE PROJECT MODAL */}
      <Modal
        title="Delete Project"
        isVisible={deleteProjectModal}
        onClose={closeDeleteModal}
      >

        <div className="pt-4 text-sm">
          Are you sure you want to delete{' '}
          <strong>
            {selectedProject?.name}
          </strong>
          ?
        </div>

        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Deleting this project will remove
          all resources associated with it.
        </p>

        <div className="flex justify-end gap-3 pt-6">

          <button
            type="button"
            onClick={closeDeleteModal}
            disabled={loading}
            className="px-5 py-2 rounded-md border dark:border-[#c4c4c432] text-sm disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            type="button"
            disabled={loading}
            onClick={deleteProject}
            className="bg-red-500 text-white text-sm px-5 py-2 rounded-md hover:bg-red-600 disabled:opacity-50"
          >
            {loading
              ? 'Deleting...'
              : 'Delete Project'}
          </button>

        </div>

      </Modal>

    </div>
  );
};

export default ManageProject;